<template>
    <div>
        <modal id="priceAlertModal" v-if="showModal" @close="handleClose" ref="modalRef">
            <template #header>
                <span v-if="action == 'add-to-price-alerts'">Add to Price Alerts</span>
                <span v-else-if="action == 'edit-price-alerts'">Edit Existing Price Alerts</span>
                <span v-else>{{ ucword(action) }} {{ label }} Price Alert</span>
            </template>

            <template #body>
                <div class="py-2">
                    <div id="priceAlertSelectors" class="flex flex-wrap mb-4" v-if="!priceAlertItem && !priceAlertSymbol">
                        <div class="w-full" v-if="section === 'futures' && quotes">
                            <div class="text-sm mb-1 font-bold">Select Contract:</div>
                            <div class="selector-wrapper">
                                <contract-selector
                                    v-model="symbol"
                                    :initial="quotes.symbol"
                                    :all-contracts="quotes.futures">
                                </contract-selector>
                            </div>
                        </div>

                        <div class="w-full" v-if="(section === 'forex' || section === 'crypto') && quotes">
                            <div class="text-sm mb-1 font-bold">Select Crossrate:</div>
                            <div class="selector-wrapper">
                                <forex-selector :symbols="quotes.forex || quotes.crypto" v-model="symbol" />
                            </div>
                        </div>

                        <div class="w-full" v-if="section === 'future-spreads' && quotes">
                            <div class="text-sm mb-1 font-bold">Select Futures Spread:</div>
                            <div class="selector-wrapper">
                                <spread-selector :spreads="quotes.spreads" :all-contracts="quotes.futures" v-model="symbol" />
                            </div>
                        </div>

                        <div class="w-full" v-if="section === 'future-options' && quotes">
                            <div class="text-sm mb-1 font-bold">Select Strike:</div>
                            <div class="selector-wrapper">
                                <option-selector-2 v-model="symbol" :groups="quotes.options" />
                            </div>
                        </div>

                        <div class="w-full" v-if="section === 'stock-etf'">
                            <div class="text-sm mb-1 font-bold">Enter a symbol:</div>
                            <div class="selector-wrapper">
                                <stock-search-selector v-model="symbol" />
                            </div>
                        </div>
                    </div>

                    <div v-if="lastPriceQuotes" class="flex flex-wrap">
                        <div class="w-full bg-tan-200 text-center py-2 border mb-2 border-gray-200">
                            <span v-if="!updating">
                                <div class="mb-1" v-if="priceAlertSymbol">
                                    <p class="text-md font-bold leading-none mb-1 text-blue-500">{{ lastPriceQuotes.contractFullName || lastPriceQuotes.symbolName }}</p>
                                </div>
                                <div class="mb-1" v-if="priceAlertItem">
                                    <p class="text-md font-bold leading-none mb-1 text-blue-500">{{ priceAlertItem.contractFullName || priceAlertItem.symbol }}</p>
                                </div>
                                <p class="text-sm font-bold leading-none mb-1">Current Price {{ lastPriceQuotes.lastPrice }}</p>
                                <p class="text-sm text-gray-600 leading-none">as of {{ lastPriceQuotes.tradeTime }}</p>
                            </span>
                            <spinner class="-my-1" v-else></spinner>
                        </div>

                        <div class="w-full p-0 mb-3 bg-red-500 px-2 py-1 text-white text-xs text-center" v-if="formError">
                            {{ formError }}
                        </div>

                        <template v-if="!(priceAlerts && priceAlerts[symbol] && action == 'add')">
                            <div class="w-full my-2">
                                <p class="text-xs leading-tight">Check the boxes below to activate or deactivate an alert. Enter the number you want the price to cross to trigger the alert using the format shown above.</p>
                                <p class="text-xs leading-tight mt-1"><em>Intraday</em> checks price during trading hours and will deactivate if triggered.</p>
                                <p class="text-xs leading-tight mt-1"><em>End-of-Day</em> checks price at 5:30pm CT and will continue to trigger unless deactivated.</p>
                            </div>
                            <div class="w-full mb-2">
                                <table class="table table-hover price-alerts-table-modal">
                                    <thead>
                                        <tr>
                                            <th>Active</th>
                                            <th>Last Price</th>
                                            <th v-if="action == 'edit' || action == 'edit-price-alerts'">Last Triggered</th>
                                            <th>Frequency</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <!-- Price Above -->
                                        <tr :class="{'disabled': form.above.action != 'Active'}">
                                            <td>
                                                <span v-if="form.above.action == 'Active' || form.above.action == 'Inactive' || form.above.action == 'Triggered'">
                                                    <i @click="toggleAlertAction('above')"
                                                        :class="{
                                                            'fas fa-check-square text-blue-500': form.above.action == 'Active',
                                                            'far fa-square text-gray-400': form.above.action == 'Inactive' || form.above.action == 'Triggered',
                                                        }"
                                                        class="text-2xl cursor-pointer"
                                                    ></i>
                                                </span>
                                                <span v-if="form.above.action == 'Starting' || form.above.action == 'Stopping'">
                                                    <i class="fas fa-circle-notch fa-spin text-yellow-500 text-lg cursor-pointer"></i>
                                                </span>
                                            </td>
                                            <td nowrap>
                                                <span class="mr-1"><strong>Above</strong></span>
                                                <label for="minutes" class="leading-loose align-middle inline-flex text-xs">
                                                    <price-input
                                                        :symbol="symbol"
                                                        id="priceAbove"
                                                        v-model="form.above.lastPrice"
                                                        :base-code="lastPriceQuotes.baseCode"
                                                        :symbol-type="lastPriceQuotes.symbolType"
                                                        :default-value="form.above.lastPrice"
                                                        :class="{'is-invalid': errors.priceAbove == true, 'form-leading-none': action != 'add'}"
                                                        class="form-input-inline-w-none form-input-sm-inline text-xs"
                                                    ></price-input>
                                                </label>
                                            </td>
                                            <td v-if="action == 'edit' || action == 'edit-price-alerts'">{{ toDateString(form.above.lastTriggered) }}</td>
                                            <td :class="{'disabled': form.above.action != 'Active'}">
                                                <select class="form-control form-control-sm form-control-no-shadow" v-model="form.above.checkType">
                                                    <option value="intraday" :selected="form.above.checkType == 'intraday'">Intraday</option>
                                                    <option value="eod" :selected="form.above.checkType == 'eod'">End-of-Day</option>
                                                </select>
                                            </td>
                                        </tr>
                                        <!-- /Price Above -->
                                        <!-- Price Below -->
                                        <tr :class="{'disabled': form.below.action != 'Active'}">
                                            <td>
                                                <span v-if="form.below.action == 'Active' || form.below.action == 'Inactive' || form.below.action == 'Triggered'">
                                                    <i @click="toggleAlertAction('below')"
                                                        :class="{
                                                            'fas fa-check-square text-blue-500': form.below.action == 'Active',
                                                            'far fa-square text-gray-400': form.below.action == 'Inactive' || form.below.action == 'Triggered',
                                                        }"
                                                        class="text-2xl cursor-pointer"
                                                    ></i>
                                                </span>
                                                <span v-if="form.below.action == 'Starting' || form.below.action == 'Stopping'">
                                                    <i class="fas fa-circle-notch fa-spin text-yellow-500 text-lg cursor-pointer"></i>
                                                </span>
                                            </td>
                                            <td nowrap>
                                                <span class="mr-1"><strong>Below</strong></span>
                                                <label for="minutes" class="leading-loose align-middle inline-flex text-xs">
                                                    <price-input
                                                        :symbol="symbol"
                                                        id="priceBelow"
                                                        v-model="form.below.lastPrice"
                                                        :base-code="lastPriceQuotes.baseCode"
                                                        :symbol-type="lastPriceQuotes.symbolType"
                                                        :default-value="form.below.lastPrice"
                                                        :class="{'is-invalid': errors.priceBelow == true, 'form-leading-none': action != 'add'}"
                                                        class="form-input-inline-w-none form-input-sm-inline text-xs"
                                                    ></price-input>
                                                </label>
                                            </td>
                                            <td v-if="action == 'edit' || action == 'edit-price-alerts'">{{ toDateString(form.below.lastTriggered) }}</td>
                                            <td :class="{'disabled': form.below.action != 'Active'}">
                                                <select class="form-control form-control-sm form-control-no-shadow" v-model="form.below.checkType">
                                                    <option value="intraday" :selected="form.below.checkType == 'intraday'">Intraday</option>
                                                    <option value="eod" :selected="form.below.checkType == 'eod'">End-of-Day</option>
                                                </select>
                                            </td>
                                        </tr>
                                        <!-- /Price Below -->
                                    </tbody>
                                </table>
                            </div>
                            <div class="w-full" v-if="action == 'add-to-price-alerts' || action == 'edit-price-alerts'">
                                <p class="text-center"><a class="text-sm font-bold" :href="getLink()">View all «Price Alerts» and «Notification Preferences»</a></p>
                            </div>
                        </template>
                        <div class="w-full p-0 mb-3 bg-yellow-500 px-2 py-1 text-white text-xs text-center" v-else>
                            You have already set an alert for this symbol.<br />Please edit alert from the table on the «<a class="text-sm font-bold text-white hover:text-white" :href="getLink()">Price Alerts</a>» page.
                        </div>

                    </div>
                    <spinner v-else-if='showSpinner && !errorSymbolQuotes'></spinner>

                    <template v-if="errorSymbolQuotes">
                        <alert-box class="mb-6">
                            <template #body>
                                Error retrieving information.<br/>This symbol has expired or is invalid.
                            </template>
                        </alert-box>
                    </template>
                </div>
            </template>

            <template #footer>
                <div class="flex justify-center">
                    <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="handleClose">Cancel</div>
                    <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="save" v-if="!errorSymbolQuotes" :class="{'btn-disabled': !hasChanged}">
                        <span v-if="action == 'add-to-price-alerts'">Add to «Price Alerts»</span>
                        <span v-else-if="action == 'edit-price-alerts'">Update</span>
                        <span v-else-if="action == 'edit'">Save</span>
                        <span v-else>{{ ucword(action) }}</span>
                    </div>
                </div>
            </template>
        </modal>
        <flash-message ref="priceAlertFlash" :seconds-to-display="6">
            <template #body>
                <div v-if="flashError">
                    Error adding to Price Alerts
                </div>
                <div class="text-center" v-else>
                    <div>
                        Success! {{ priceAlertSymbol }} has been added to your «Price Alerts»
                    </div>

                    <div>
                        <a class="text-xs" :href="getLink()">
                            Go to Price Alerts
                        </a>
                    </div>
                </div>
            </template>
        </flash-message>
    </div>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue';
import PriceInput from '../../PriceInput';

import Profile from '@barchart/marketdata-api-js/lib/marketState/Profile';
import stringFormatter from '@barchart/marketdata-api-js/lib/utilities/format/specialized/cmdtyView';
// import priceFormatter from '@barchart/marketdata-api-js/lib/utilities/parse/price';

// Props
const props = defineProps({
    priceAlerts: {
        type: Object,
        default: () => ({}),
        required: false,
    },
    priceAlert: {
        type: Object,
        default: () => ({}),
        required: false,
    },
});

const emit = defineEmits(['onModalAction', 'close']);

const action = ref(props.priceAlert.action || 'add');
const section = ref(props.priceAlert.section ? props.priceAlert.section.name : '');
const label = ref(props.priceAlert.section ? props.priceAlert.section.label : '');
const priceAlertItem = ref(props.priceAlert.alertItem || null);
const priceAlertSymbol = ref(props.priceAlert.symbol || '');

const symbol = ref(null);
const quotes = ref(null);

const lastPriceQuotes = ref(null);
const updating = ref(false);
const showSpinner = ref(true);
const showModal = ref(true);

const hasChanged = ref(false);

const initForm = ref(null);
const form = ref({
    'above': { alertId: null, action: 'Inactive', lastTriggered: null, lastPrice: null, lastPriceFormatted: null, priceType: 'above', checkType: 'intraday' },
    'below': { alertId: null, action: 'Inactive', lastTriggered: null, lastPrice: null, lastPriceFormatted: null, priceType: 'below', checkType: 'intraday' }
});

const formError = ref(false);
const errors = ref({ 'priceAbove': null, 'priceBelow': null });
const flashError = ref(false);
const errorSymbolQuotes = ref(false);

watch(symbol, () => {
    getLastPriceForSymbol();
});

watch(form, (newForm, oldForm) => {
    hasChanged.value = true;
    if (action.value === 'edit') {
        hasChanged.value = false;
        if (JSON.stringify(newForm) !== JSON.stringify(initForm.value)) {
            hasChanged.value = true;
        }
    }
}, { deep: true });

watch(() => form.value.above.lastPrice, (newValue) => {
    if (lastPriceQuotes.value) {
        let formattedValue = newValue;
        if (lastPriceQuotes.value.baseCode) {
            const profile = new Profile(symbol.value, symbol.value, '', lastPriceQuotes.value.baseCode, 1000, 1);
            formattedValue = stringFormatter(newValue, lastPriceQuotes.value.baseCode, profile);
            // formattedValue = priceFormatter(newValue, lastPriceQuotes.value.baseCode);
            // formattedValue = window.Barchart.Utilities.priceFormatter('-', true, ',').format(newValue, lastPriceQuotes.value.baseCode);
        }
        form.value.above.lastPriceFormatted = formattedValue;
    }
}, { deep: true });

watch(() => form.value.below.lastPrice, (newValue) => {
    if (lastPriceQuotes.value) {
        let formattedValue = newValue;
        if (lastPriceQuotes.value.baseCode) {
            const profile = new Profile(symbol.value, symbol.value, '', lastPriceQuotes.value.baseCode, 1000, 1);
            formattedValue = stringFormatter(newValue, lastPriceQuotes.value.baseCode, profile);
            // formattedValue = window.Barchart.Utilities.priceFormatter('-', true, ',').format(newValue, lastPriceQuotes.value.baseCode);
        }
        form.value.below.lastPriceFormatted = formattedValue;
    }
}, { deep: true });

watch(() => props.priceAlerts, (newPriceAlerts) => {
    if (action.value === 'add-to-price-alerts' && newPriceAlerts) {
        if (newPriceAlerts[symbol.value]) {
            priceAlertItem.value = newPriceAlerts[symbol.value];
            action.value = 'edit-price-alerts';
            priceAlertItem.value.forEach(a => {
                let priceType = a.notes.priceType;
                Object.keys(form.value[priceType]).forEach((key) => {
                    if (key == 'action') {
                        form.value[priceType][key] = a.alert_state;
                    } else if (key == 'lastTriggered') {
                        form.value[priceType][key] = a.last_trigger_date;
                    } else if (key == 'alertId') {
                        form.value[priceType][key] = a.alert_id;
                    } else {
                        form.value[priceType][key] = a.notes[key];
                    }
                });
            });
            initForm.value = JSON.parse(JSON.stringify(form.value));
        }
    }
}, { deep: true });

function ucword(text) {
    return text.charAt(0).toUpperCase() + text.slice(1)
}

function toDateString(value) {
    if (!value) {
        return '-';
    }
    return new Date(parseInt(value)).toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric', hour12: false }) + ' CT';
}

function toggleAlertAction(name) {
  form.value[name].action = (form.value[name].action === 'Inactive' || form.value[name].action === 'Triggered') ? 'Active' : 'Inactive';
}

function getLink() {
    return window.route('my-folder.price-alerts.index');
}

const save = () => {
    formError.value = false;

    if (!form.value.above.lastPrice && !form.value.below.lastPrice) {
        formError.value = 'Please enter the trigger value in the price format shown above.';
        return;
    }

    if (form.value.above.action === 'Active' && !form.value.above.lastPrice) {
        formError.value = 'Please enter a value for the active alert or uncheck the box to deactivate it.';
        return;
    }

    if (form.value.below.action === 'Active' && !form.value.below.lastPrice) {
        formError.value = 'Please enter a value for the active alert or uncheck the box to deactivate it.';
        return;
    }

    if (hasChanged.value) {
        form.value.symbol = symbol.value;
        form.value.section = section.value;
        form.value.contractName = lastPriceQuotes.value.contractFullName || lastPriceQuotes.value.symbolName || symbol.value;

        if (action.value === 'add' || action.value === 'add-to-price-alerts') {
            emit('onModalAction', { action: 'add', data: form.value });
        } else if (action.value === 'edit' || action.value === 'edit-price-alerts') {
            emit('onModalAction', { action: 'edit', data: form.value, item: priceAlertItem.value });
        }
    } else {
        emit('onModalAction', { action: 'close' });
    }
};

function getQuotesForSelects() {
    const route = window.route('api.quotes.symbol-select');
    const params = { section: section.value };

    axios.post(route, params)
        .then((response) => {
            quotes.value = response.data;
        }).catch((error) => {
            console.error('Could not retrieve quotes');
        });
}

function getLastPriceForSymbol() {
    updating.value = true;

    const route = window.route('api.quotes');
    const requestParams = {
        symbol: symbol.value,
        fields: 'symbol,symbolName,contractFullName,lastPrice,tradeTime,baseCode,symbolType',
        after: 'getSection'
    };

    axios.post(route, requestParams)
        .then((response) => {
            if (response.data.errors && response.data.errors.invalidSymbols) {
                errorSymbolQuotes.value = true;
            }
            updating.value = false;
            lastPriceQuotes.value = response.data.data[0];
            if (lastPriceQuotes.value && lastPriceQuotes.value['section']) {
                section.value = lastPriceQuotes.value['section'];
            }
        }).catch((error) => {
            console.error('Error fetching last price for symbol:', error);
            updating.value = false;
            errorSymbolQuotes.value = true;
        });
}

const handleClose = () => {
    emit('close');
};

onMounted(() => {
    if (section.value === 'stock-etf') {
        showSpinner.value = false;
    }

    if (action.value === 'add' || action.value === 'add-to-price-alerts') {
        if (action.value === 'add-to-price-alerts') {
            symbol.value = props.priceAlert.symbol;
        }
        getQuotesForSelects();
    }

    if (action.value === 'edit') {
        symbol.value = priceAlertItem.value[0].notes.symbol;
        priceAlertItem.value.forEach(a => {
            let priceType = a.notes.priceType;
            Object.keys(form.value[priceType]).forEach(key => {
                if (key === 'action') {
                    form.value[priceType][key] = a.alert_state;
                } else if (key === 'lastTriggered') {
                    form.value[priceType][key] = a.last_trigger_date;
                } else if (key === 'alertId') {
                    form.value[priceType][key] = a.alert_id;
                } else {
                    form.value[priceType][key] = a.notes[key];
                }
            });
        });
        initForm.value = JSON.parse(JSON.stringify(form.value));
    }
});
</script>


<style lang="scss">
#priceAlertModal {
    @apply text-left;

    .price-alerts-table-modal {
        &.table {
            width: 100% !important;
            min-width: unset !important;

            & * {
                td, th {
                    border-left: none !important;
                    border-right: none !important;
                    border-bottom: none !important;
                }
                background-color: transparent !important;
            }

            thead,
            tfoot {
                td, th {
                    padding: .5rem .25rem !important;
                    font-size: .725rem !important;
                    text-align: center !important;
                }
            }
            thead {
                tr {
                    border-bottom: 2px solid #cccccc !important;
                    td, th {
                        border-top: 1px solid #cccccc !important;
                        border-bottom: 1px solid #cccccc !important;
                    }
                }
            }
            tbody {
                tr.disabled {
                        td:not(:first-child) {
                            input, select {
                                background-color: #eeeeee !important;
                            }
                            pointer-events: none !important;
                            cursor: not-allowed !important;
                        }
                }
                td, th {
                    padding: .5rem .25rem !important;
                    font-size: .725rem;
                    line-height: 1.25;
                    vertical-align: middle;
                    text-align: center !important;
                }
            }

            .form-control {
                margin-bottom: 0 !important;
            }

            .form-leading-none {
                line-height: 1 !important;
            }
        }

        input[type=text] {
            @screen xs {
                width: 50% !important;
            }
            @screen sm {
                width: 100% !important;
            }
        }

        input[type=text],
        select {
            font-size: .75rem;
        }
    }

    input {
        box-shadow: none !important;
    }

    .modal-container {
        width: 36rem;
        @screen xs {
            width: 100%;
        }
    }

    sup {
        @apply text-red-500;
    }

    #priceAlertSelectors {
        .selector-wrapper {
            & > span > div {
                margin-right: 0;
            }
            .inline-block {
                width: 100% !important;
            }

            .clContactRadioSelector {
                @apply text-right;
                input[type="radio"] {
                    @apply ml-2;
                }
                .inline-block {
                    width: auto !important;
                }
            }

            select.form-control-fixed {
                @apply mb-2;
                width: 100% !important;
            }
        }
    }
}
</style>
